<template>
  <div class="billing-checkout">
    <v-container>
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <!-- Affiliation Banner -->
          <v-img
            v-if="banner"
            :src="banner"
            class="affiliation-banner mb-4"
            max-height="200"
            contain
          ></v-img>

          <v-card>
            <v-card-text>
              <!-- Loading state -->
              <div v-if="loading" class="text-center py-8">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                ></v-progress-circular>
                <p class="mt-4">Preparando checkout...</p>
              </div>

              <!-- Error state -->
              <v-alert v-if="error" type="error" class="mb-4">
                {{ error }}
              </v-alert>

              <!-- Stripe Embedded Checkout Container -->
              <div 
                v-show="!loading && !error" 
                id="checkout"
                ref="checkoutElement"
              ></div>

              <!-- One-time payment option -->
              <div v-show="!loading && !error && !subscriptionOnly" class="text-center mt-4 pt-4 border-top">
                <p class="subtitle-1 text--secondary mb-3">
                  ¿No quieres suscribirte?
                </p>
                <v-btn
                  @click="goToOneTimePayment"
                  color="primary"
                  depressed
                  x-large
                  class="one-time-payment-btn"
                >
                  Pagar solo por esta vez
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

export default {
  name: 'BillingCheckout',
  
  data() {
    return {
      banner: null,
      stripe: null,
      checkout: null,
      loading: true,
      error: null,
      clientSecret: null,
      apiKey: process.env.VUE_APP_STRIPE_API_KEY,
      stripeAccount: null, // Para usar con Stripe Connect si es necesario
      subscriptionOnly: false,
    };
  },

  async mounted() {
    await this.fetchAffiliationTypeConfig();
    await this.initializeCheckout();
    await this.fetchAffiliationBanner();
  },

  beforeDestroy() {
    // Limpiar el checkout cuando el componente se destruye
    if (this.checkout) {
      this.checkout.destroy();
    }
  },

  methods: {
    async fetchAffiliationTypeConfig() {
      try {
        const affiliationTypeId = this.$route.params.affiliationType;
        
        const response = await this.axios({
          method: 'GET',
          url: `/affiliation-types/single/${affiliationTypeId}`,
        });

        const affiliationType = response.data.affiliationType || response.data;
        
        // Obtener subscriptionOnly desde subscriptionConfig del nivel de membresía
        this.subscriptionOnly = affiliationType.subscriptionConfig?.subscriptionOnly || false;
      } catch (error) {
        console.error('Error fetching affiliation type config:', error);
        // Si hay error, permitir ambas opciones por defecto
        this.subscriptionOnly = false;
      }
    },

    goToOneTimePayment() {
      // Redirigir al checkout normal (convencional) para pago único
      this.$router.push({
        name: "Member new Checkout",
        params: {
          affiliationId: this.$route.params.affiliationId,
          affiliationType: this.$route.params.affiliationType,
          membershipId: this.$route.params.membershipId,
          organization: this.$route.params.organization,
        },
      });
    },

    async initializeCheckout() {
      try {
        this.loading = true;
        this.error = null;

        const clientSecret = await this.createCheckoutSession();
        
        if (!clientSecret) {
          throw new Error('No se pudo obtener el client secret');
        }

        this.clientSecret = clientSecret;

        // 2. Cargar Stripe
        const stripeOptions = {};
        if (this.stripeAccount) {
          stripeOptions.stripeAccount = this.stripeAccount;
        }

        this.stripe = await loadStripe(this.apiKey, stripeOptions);

        // 3. Inicializar Embedded Checkout
        this.checkout = await this.stripe.initEmbeddedCheckout({
          clientSecret: this.clientSecret,
        });

        // 4. Montar el checkout en el DOM
        this.checkout.mount('#checkout');

        this.loading = false;
      } catch (err) {
        console.error('Error al inicializar checkout:', err);
        this.error = err.message || 'Error al cargar el checkout';
        this.loading = false;
      }
    },

    async createCheckoutSession() {
      try {
        const response = await this.axios({
          method: 'POST',
          url: '/billing/create-checkout-session',
          data: {
            affiliationTypeId: this.$route.params.affiliationType,
          },
        })

        console.log('Checkout session created:', response.data);

        return response.data.session.client_secret;
      } catch (err) {
        console.error('Error al crear checkout session:', err);
        throw new Error('No se pudo crear la sesión de checkout');
      }
    },

    async handlePaymentSuccess() {
      // Este método se puede usar si necesitas hacer algo después del pago exitoso
      // Stripe redirigirá automáticamente según la configuración de tu sesión
      console.log('Pago procesado exitosamente');
    },

    async fetchAffiliationBanner() {
      try {
        const response = await this.axios({
          method: "GET",
          url: `/affiliations/${this.$route.params.affiliationId}/banner`,
        });

        this.banner = response.data.banner;
      } catch (error) {
        console.error("Error fetching affiliation banner:", error);
      }
    },
  },
};
</script>

<style scoped>
.billing-checkout {
  min-height: 100vh;
  padding: 20px 0;
  background-color: #f5f5f5;
}

#checkout {
  min-height: 400px;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}

.one-time-payment-btn {
  height: 48px !important;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.3px;
  border-radius: 6px;
}

.affiliation-banner {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>